import { NavLink } from 'libs/router'
import { useTranslation } from 'libs/i18n'
import { CompactTable } from '@table-library/react-table-library/compact'
import { useTheme } from '@table-library/react-table-library/theme'
import { getTheme } from '@table-library/react-table-library/baseline'
import { ReceiptPercentIcon } from '@heroicons/react/24/outline'
import { Currency } from 'shared/components'
import { EmptyState } from 'shared/components/EmptyState/EmptyState'
import { getCardTableStyles, type CardTableColumn } from 'styles/table'
import { format, parseISO } from 'libs/dates'
import { routes } from 'config/routes'

export interface ProductSale {
	id: string
	created: string
	receipt_reference: string
	product_name: string
}

export interface ProductSalesTableProps {
	sales: ProductSale[]
	product_id: number
}

const numeric = 'block text-right tabular-nums'

export const ProductSalesTable = ({ sales, product_id }: ProductSalesTableProps) => {
	const { t } = useTranslation()

	const columns: CardTableColumn[] = [
		{
			label: t('Date'),
			width: '110px',
			renderCell: (item: any) => (
				<NavLink className="font-medium text-primary hover:underline" to={routes.saleEdit(item.id)}>
					{format(parseISO(item.created), 'dd.MM.yyyy')}
				</NavLink>
			)
		},
		{
			label: t('Reference'),
			width: 'minmax(100px, 1fr)',
			renderCell: (item: any) => (
				<span className="text-muted-foreground">{item.receipt_reference}</span>
			)
		},
		{
			label: t('Price'),
			width: '100px',
			align: 'right',
			renderCell: (item: any) => <Currency className={numeric} price={item.product_unit_price} />
		},
		{
			label: t('Quantity'),
			width: '80px',
			align: 'right',
			renderCell: (item: any) => <span className={numeric}>{item.quantity}</span>
		},
		{
			label: t('Discount'),
			width: '100px',
			align: 'right',
			renderCell: (item: any) => <Currency className={numeric} price={item.discount} />
		},
		{
			label: t('Subtotal'),
			width: '100px',
			align: 'right',
			renderCell: (item: any) => <Currency className={numeric} price={item.subtotal} />
		},
		{
			label: t('Commission'),
			width: '130px',
			align: 'right',
			renderCell: (item: any) => (
				<span className={numeric}>
					<Currency price={item.commission_amount} />{' '}
					<span className="text-muted-foreground">({+item.commission_percentage}%)</span>
				</span>
			)
		}
	]

	const theme = useTheme([getTheme(), getCardTableStyles(columns, { selectable: false })])

	return (
		<section className="overflow-hidden rounded-lg border border-border bg-card shadow-panel">
			<div className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
				<h2 className="font-display text-base font-semibold text-foreground">{t('Sales')}</h2>
				{sales.length > 0 && (
					<NavLink
						className="text-[12px] font-medium text-primary hover:underline"
						to={`${routes.saleIndex}?product_id=${product_id}`}>
						{t('View all')}
					</NavLink>
				)}
			</div>
			{sales.length ? (
				<div className="[&_table]:static">
					<CompactTable
						columns={columns}
						data={{ nodes: sales }}
						theme={theme}
						layout={{ custom: true, horizontalScroll: true }}
					/>
				</div>
			) : (
				<EmptyState icon={<ReceiptPercentIcon />} text={t('No sales yet')} />
			)}
		</section>
	)
}
